import { useState, useRef } from 'react';
import { Bold, Italic, List, Link, Image, Heading2 } from 'lucide-react';

import { API_URL } from '../../config/api';

/**
 * Simple Rich Text Editor using textarea with formatting buttons
 * No external dependencies, no CSP issues
 */
const RichTextEditor = ({ value, onChange, height = 400, placeholder = 'Escribe aquí...' }) => {
    const textareaRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const wrapSelection = (before, after = before) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = value || '';
        const selectedText = text.substring(start, end);

        const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
        onChange(newText);

        // Re-focus and select the modified text
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + before.length, end + before.length);
        }, 0);
    };

    const insertAtCursor = (text) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const currentText = value || '';
        const newText = currentText.substring(0, start) + text + currentText.substring(start);
        onChange(newText);

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + text.length, start + text.length);
        }, 0);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'blog');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const data = await response.json();
            if (data.success) {
                insertAtCursor(`\n![Imagen](${API_URL}${data.data.url})\n`);
            } else {
                alert('Error al subir: ' + (data.message || 'Error desconocido'));
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Error de conexión al subir imagen');
        } finally {
            setUploading(false);
        }
    };

    const handleLink = () => {
        const url = prompt('URL del enlace:');
        if (url) {
            const textarea = textareaRef.current;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = value || '';
            const selectedText = text.substring(start, end) || 'Texto del enlace';

            const newText = text.substring(0, start) + `[${selectedText}](${url})` + text.substring(end);
            onChange(newText);
        }
    };

    return (
        <div className="simple-editor">
            <div className="editor-toolbar">
                <button type="button" onClick={() => wrapSelection('**')} title="Negrita">
                    <Bold size={16} />
                </button>
                <button type="button" onClick={() => wrapSelection('*')} title="Cursiva">
                    <Italic size={16} />
                </button>
                <button type="button" onClick={() => wrapSelection('\n## ', '\n')} title="Título">
                    <Heading2 size={16} />
                </button>
                <button type="button" onClick={() => insertAtCursor('\n- ')} title="Lista">
                    <List size={16} />
                </button>
                <button type="button" onClick={handleLink} title="Enlace">
                    <Link size={16} />
                </button>
                <label className="toolbar-btn" title="Insertar imagen">
                    <Image size={16} />
                    {uploading && <span className="uploading">...</span>}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        hidden
                        disabled={uploading}
                    />
                </label>
            </div>
            <textarea
                ref={textareaRef}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                style={{ height, minHeight: height }}
                className="editor-textarea"
            />
            <div className="editor-help">
                Usa **texto** para negrita, *texto* para cursiva, ## para títulos
            </div>
        </div>
    );
};

export default RichTextEditor;
